import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ProductForm from "./ProductForm";
import ProductVariantForm from "./OptionalForm";
import { getProductBySlug, editProduct } from "../../api/productService";
import { getErrorMessage } from "../../utils/errorHandler";
import SeoFields from "../../common/SEOFields";
import { updateSeo } from "../../api/SeoService";

export default function EditProductStepper() {
  const location = useLocation();
  const navigate = useNavigate();

  const productId = location?.state?.productId;
  const slug = location?.state?.slug;

  const storageKey = `editProductStep_${productId}`;

  const [step, setStep] = useState(() => {
    return Number(localStorage.getItem(storageKey)) || 1;
  });

  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [success, setSuccess] = useState(false);
  const [backendErrors, setBackendErrors] = useState({});
  const [seoLoading, setSeoLoading] = useState(false);
  const [seoFormData, setSeoFormData] = useState({
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    metaImage: null,
    metaImagePreview: "",
  });
  const [seoErrors, setSeoErrors] = useState({});
  const [seoImages, setSeoImages] = useState([]);

  // ================= SAVE STEP =================
  useEffect(() => {
    if (productId) {
      localStorage.setItem(storageKey, step);
    }
  }, [step, storageKey, productId]);

  // ================= FETCH PRODUCT =================
  useEffect(() => {
    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  const fetchProduct = async () => {
    try {
      setLoading(true);

      const response = await getProductBySlug(slug);
      setProduct(response?.data);
    } catch (error) {
      console.log("FETCH PRODUCT ERROR:", error);
      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (product?.seo) {
      setSeoFormData({
        metaTitle: product.seo.metaTitle || "",
        metaDescription: product.seo.metaDescription || "",
        metaKeywords: Array.isArray(product.seo.metaKeywords)
          ? product.seo.metaKeywords.join(", ")
          : product.seo.metaKeywords || "",
        metaImage: product.seo.metaImage || null,
        metaImagePreview: product.seo.metaImage || "",
      });
    }
  }, [product]);

  // ================= UPDATE PRODUCT =================
  const handleUpdateProduct = async (formData) => {
    try {
      setLoading(true);
      setBackendErrors({});
      const payload = {
        ...formData,

        coupons: Array.isArray(formData.coupons)
          ? formData.coupons.map(String)
          : formData.coupons
            ? [String(formData.coupons)]
            : [],

        status: formData.status,
      };

      const response = await editProduct(productId, payload);
      console.log("edit:", response);
      if (response?.success === false || response?.status === 422) {
        setBackendErrors(response?.error || {});
        return;
      }

      // Reload latest product data
      await fetchProduct();

      setStep(2);
    } catch (error) {
      if (error?.response?.status === 422) {
        setBackendErrors(error.response.data?.error || {});
        return;
      }

      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // ================= SEO HANDLERS =================
  const handleSeoChange = (e) => {
    const { name, value } = e.target;
    setSeoFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setSeoErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSeoImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSeoFormData((prev) => ({
      ...prev,
      metaImage: file,
      metaImagePreview: URL.createObjectURL(file),
    }));
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSeoImagesChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 5) {
      alert("You can select maximum 5 images.");
      e.target.value = "";
      return;
    }
    try {
      const base64Images = await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          type: file.type,
          data: await fileToBase64(file),
        })),
      );
      setSeoImages(base64Images);
    } catch (error) {
      console.error("Image conversion error:", error);
    }
  };

  const handleUpdateSeo = async (e) => {
    e.preventDefault();
    try {
      setSeoLoading(true);
      setSeoErrors({});
      if (!productId) {
        alert("Product ID is missing.");
        return;
      }
      let metaImage = "";
      if (seoFormData.metaImage instanceof File) {
        metaImage = await fileToBase64(seoFormData.metaImage);
      } else if (typeof seoFormData.metaImage === "string") {
        metaImage = seoFormData.metaImage;
      }
      const payload = {
        referenceId: String(productId),
        referenceType: "Product",
        metaTitle: seoFormData.metaTitle?.trim() || "",
        metaDescription: seoFormData.metaDescription?.trim() || "",
        metaKeywords: seoFormData.metaKeywords
          ? seoFormData.metaKeywords
              .split(",")
              .map((keyword) => keyword.trim())
              .filter(Boolean)
          : [],
        metaImage: metaImage,
        images: seoImages.map((image) => image.data),
        status: product?.status,
      };
      const response = await updateSeo(payload);
      if (response?.status === 422) {
        setSeoErrors(response.error || {});
        return;
      }
      if (!response?.success) {
        alert(response?.message || "SEO update failed");
        return;
      }
      setStep(3);
    } catch (error) {
      console.error("SEO ERROR:", error);
      alert(getErrorMessage(error));
    } finally {
      setSeoLoading(false);
    }
  };

  // ================= IMAGE SUCCESS =================
  const handleImagesSuccess = () => {
    setStep(3);
  };

  // ================= VARIANT SUCCESS =================
  const handleVariantSuccess = () => {
    setSuccess(true);

    localStorage.removeItem(storageKey);

    setTimeout(() => {
      navigate("/products");
    }, 1500);
  };

  // ================= LOADING =================
  if (loading && !product) {
    return (
      <div className="container py-5 text-center">
        <h5>Loading Product...</h5>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="card  border-2">
        <div className="card-body">
          

          {/* STEPPER */}
          <div className="d-flex justify-content-between align-items-center mb-4 position-relative">
            <div
              style={{
                position: "absolute",
                top: "22px",
                left: "8%",
                width: "82%",
                height: "4px",
                background: "#dee2e6",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  width: step === 1 ? "0%" : step === 2 ? "50%" : "100%",
                  height: "100%",
                  background: "#59b71f",
                  transition: "0.3s",
                }}
              />
            </div>

            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="text-center position-relative"
                style={{ zIndex: 2 }}
              >
                <div
                  style={{
                    width: "45px",
                    height: "45px",
                    borderRadius: "50%",
                    background: step >= item ? "#59b71f" : "#dee2e6",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "18px",
                    margin: "0 auto",
                  }}
                >
                  {item}
                </div>

                <p className="mt-2 mb-0">
                  {item === 1
                    ? "Product Details"
                    : item === 2
                      ? "SEO Data"
                      : "Product Variations"}
                </p>
              </div>
            ))}
          </div>

          {/* STEP 1 */}
          {step === 1 && (
            <>
              <h5 className="mb-3">Edit Product Details</h5>

              <ProductForm
                initialData={product}
                isEdit={true}
                onSubmit={handleUpdateProduct}
                loading={loading}
                backendErrors={backendErrors}
              />
            </>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              <h5 className="mb-3">Update SEO Data</h5>

              <form onSubmit={handleUpdateSeo}>
                <SeoFields
                  formData={seoFormData}
                  setFormData={setSeoFormData}
                  handleChange={handleSeoChange}
                  errors={seoErrors}
                  backendErrors={seoErrors}
                  handleImageChange={handleSeoImageChange}
                />
                <div className="row mt-3">
                  <div className="col-md-12">
                    <label className="form-label fw-semibold">
                      SEO Images
                      <span className="text-muted"> (Maximum 5)</span>
                    </label>

                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      multiple
                      onChange={handleSeoImagesChange}
                    />

                    <small className="text-muted">
                      You can select maximum 5 images.
                    </small>

                    {seoImages.length > 0 && (
                      <div className="mt-3 d-flex flex-wrap gap-3">
                        {seoImages.map((file, index) => (
                          <div
                            key={`${file.name}-${index}`}
                            className="position-relative"
                            style={{
                              width: "120px",
                              height: "120px",
                            }}
                          >
                            <img
                              src={file.data}
                              alt={`SEO ${index + 1}`}
                              className="img-thumbnail"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={seoLoading}
                  >
                    {seoLoading ? "Saving..." : "Save SEO & Continue"}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* STEP 3 */}
          {step === 3 && !success && (
            <>
              <h5 className="mb-3">Update Product Variations</h5>

              <ProductVariantForm
                id={productId}
                initialVariants={product?.variants || []}
                isEdit={true}
                onSuccess={handleVariantSuccess}
              />
            </>
          )}

          {/* SUCCESS */}
          {success && (
            <div className="alert alert-success text-center mt-4" role="alert">
              Product updated successfully!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
